/**
 * Fetch wrapper with discriminated result (never throws on HTTP errors),
 * bearer injection and 401 handling — mirrors the eureca SDK client style.
 *
 * A base vem de env.apiBaseUrl: vazia usa a origem atual (dev, via proxy do
 * Vite); absoluta aponta para a API em outro host, que é o caso em produção
 * (SPA no Cloudflare Pages, API no cluster — ADR-018). `new URL` ignora o
 * segundo argumento quando o primeiro já é absoluto, então os dois casos
 * usam a mesma expressão.
 */
import { env } from '../config/env';
import { getImpersonationToken, isImpersonationPage } from '../auth/impersonation';
import { ACCESS_ENDED_CODE, isAccessEnded } from './access-ended';
import { getSelectedTenant } from './tenant-selection';

export interface ApiError {
  status: number;
  error: string;
  details?: unknown;
}

export type ApiResult<T> = { data: T; error: null } | { data: null; error: ApiError };

const TOKEN_KEY = 'erp.token';

/**
 * Modo keycloak: o bearer vem do keycloak-js (token em memória, renovado por
 * ele) via provider registrado no boot pelo AuthGate. Sem provider (modo
 * local), cai no localStorage como sempre.
 */
let tokenProvider: (() => string | null) | null = null;
export function setTokenProvider(fn: (() => string | null) | null) {
  tokenProvider = fn;
}

export function getToken(): string | null {
  // Aba de depuração (spec 020): SÓ o token da sessão, e nada depois que ela
  // acaba. O localStorage desta origem guarda a sessão do admin (outra aba) —
  // cair nele faria a aba de depuração falar com a API como o admin.
  if (isImpersonationPage()) return getImpersonationToken();
  if (tokenProvider) return tokenProvider();
  return localStorage.getItem(TOKEN_KEY);
}
/**
 * Cabeçalhos de identidade de TODA chamada autenticada: o bearer e, para quem
 * está em mais de uma empresa, a escolhida (`X-Tenant`). Um lugar só — antes
 * cada fetch montava o seu, e o X-Tenant teria de ser lembrado em sete.
 */
export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // na aba de depuração o tenant é o da sessão (a API recusa outro); a escolha
  // guardada no navegador é a do admin
  const tenant = isImpersonationPage() ? null : getSelectedTenant();
  if (tenant) headers['X-Tenant'] = tenant;
  return headers;
}

export function setToken(token: string | null) {
  // a aba de depuração nunca toca a sessão do admin (spec 020)
  if (isImpersonationPage()) return;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  unauthorizedHandler = fn;
}

/**
 * Spec 042: o 401 `membership_inactive` (o acesso à empresa foi encerrado) não
 * é sessão vencida — reautenticar no Keycloak, que ainda tem a sessão, só
 * devolveria a pessoa para o mesmo 401. Quem registra (AuthGate) troca a tela
 * inteira pela de acesso encerrado.
 */
let accessEndedHandler: (() => void) | null = null;
export function setAccessEndedHandler(fn: (() => void) | null) {
  accessEndedHandler = fn;
}

/** O 401: acesso encerrado (tela própria) ou sessão vencida (o handler de sempre). */
async function handleUnauthorized(res: Response): Promise<{ data: null; error: ApiError }> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* 401 sem corpo JSON: sessão vencida */
  }
  if (isAccessEnded(res.status, body)) {
    accessEndedHandler?.();
    return { data: null, error: { status: 401, error: ACCESS_ENDED_CODE } };
  }
  unauthorizedHandler?.();
  return { data: null, error: { status: 401, error: 'unauthorized' } };
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | number[] | undefined>;
  signal?: AbortSignal;
  /** Timeout do fetch (padrão 30s) — ignorado quando `signal` é fornecido.
   *  Use para chamadas mais lentas que o orçamento padrão (ex.: rotas de IA). */
  timeoutMs?: number;
}

export interface ListResult<T> {
  items: T[];
  total: number;
}

/**
 * URL absoluta para um caminho relativo da API (ex.: o mediaUrl computado no
 * servidor nas mensagens de chat) — mesma expressão de base usada por api().
 */
export function apiUrl(path: string): string {
  return new URL(`${env.apiBaseUrl}/api/v1${path}`, window.location.origin).toString();
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<ApiResult<T>> {
  const url = new URL(`${env.apiBaseUrl}/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v === undefined || v === '') continue;
    url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }

  const headers: Record<string, string> = authHeaders();
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal ?? AbortSignal.timeout(opts.timeoutMs ?? 30_000),
    });
  } catch (err) {
    return { data: null, error: { status: 0, error: err instanceof Error ? err.message : 'network_error' } };
  }

  if (res.status === 401) return handleUnauthorized(res);

  if (!res.ok) {
    let body: { error?: string; details?: unknown } = {};
    try {
      body = await res.json();
    } catch {
      /* non-json error body */
    }
    return {
      data: null,
      error: { status: res.status, error: body.error ?? `http_${res.status}`, details: body.details },
    };
  }

  if (res.status === 204) return { data: undefined as T, error: null };
  const data = (await res.json()) as T;
  return { data, error: null };
}

/**
 * Upload multipart (campo 'file'): fetch cru com FormData + bearer manual —
 * api() serializa body como JSON e não serve para multipart. Ao contrário de
 * api(), LANÇA um ApiError-like em falha (padrão dos mutationFn de upload).
 */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const headers: Record<string, string> = authHeaders();
  const url = new URL(`${env.apiBaseUrl}/api/v1${path}`, window.location.origin);
  const res = await fetch(url, { method: 'POST', headers, body: form });
  if (!res.ok) {
    let body: { error?: string } = {};
    try {
      body = (await res.json()) as { error?: string };
    } catch {
      /* corpo de erro não-JSON */
    }
    throw { status: res.status, error: body.error ?? `http_${res.status}` };
  }
  return (await res.json()) as T;
}

/**
 * Falha de download com o que a API disse: `status` e o `error` do corpo JSON
 * (quando houver), para a tela dar a mensagem certa. A mensagem continua a de
 * sempre — quem só mostra `err.message` não muda.
 */
export class DownloadError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null,
    readonly details: Record<string, unknown> | null = null
  ) {
    super(message);
    this.name = 'DownloadError';
  }
}

/**
 * Download autenticado: o endpoint exige bearer, então window.open não serve —
 * baixa via fetch, materializa em blob e dispara um <a download> programático.
 * (Sem token — página pública — o fetch segue sem Authorization.)
 *
 * `expectType`: o tipo que a resposta TEM de ter (ex.: application/pdf). Uma
 * API anterior que ignora o parâmetro novo responde 200 com JSON, e sem esta
 * guarda a pessoa salvaria um ".pdf" que não abre — com ela, o download falha
 * com `code: 'unexpected_type'` e a tela explica.
 */
export async function apiDownload(
  path: string,
  fileName: string,
  opts: { expectType?: string } = {}
): Promise<void> {
  const headers: Record<string, string> = authHeaders();
  const url = new URL(`${env.apiBaseUrl}/api/v1${path}`, window.location.origin);
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let body: { error?: string; details?: Record<string, unknown> } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      /* corpo de erro não-JSON */
    }
    throw new DownloadError(
      `Falha no download (HTTP ${res.status})`,
      res.status,
      body.error ?? null,
      body.details ?? null
    );
  }
  const type = res.headers.get('content-type') ?? '';
  if (opts.expectType && !type.toLowerCase().startsWith(opts.expectType)) {
    throw new DownloadError(`Resposta inesperada (${type || 'sem tipo'})`, res.status, 'unexpected_type');
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/** List endpoints return X-Total-Items; this variant surfaces it. */
export async function apiList<T>(
  path: string,
  opts: RequestOptions = {}
): Promise<ApiResult<ListResult<T>>> {
  const url = new URL(`${env.apiBaseUrl}/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v === undefined || v === '') continue;
    url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  const headers: Record<string, string> = authHeaders();

  let res: Response;
  try {
    res = await fetch(url, {
      headers,
      signal: opts.signal ?? AbortSignal.timeout(opts.timeoutMs ?? 30_000),
    });
  } catch (err) {
    return { data: null, error: { status: 0, error: err instanceof Error ? err.message : 'network_error' } };
  }
  if (res.status === 401) return handleUnauthorized(res);
  if (!res.ok) {
    let body: { error?: string } = {};
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    return { data: null, error: { status: res.status, error: body.error ?? `http_${res.status}` } };
  }
  const items = (await res.json()) as T[];
  const total = Number(res.headers.get('X-Total-Items') ?? items.length);
  return { data: { items, total }, error: null };
}
